import { Loader2 } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  electionOutcomeResultLabel,
  electionStageLabel,
  ElectionOutcomeResult,
  type SubsidiaryElectionOutcomeDto,
} from '@signalvote/shared';
import { useSubsidiaryElectionOutcomes } from '@/hooks/queries/useSubsidiaryElectionOutcomes';
import { formatDateTime } from './utils';

// Won reads as a positive result, Lost / Withdrew as negative / neutral — mapped to the shared
// Badge variants (theme tokens only; no custom colors, per the frontend Don'ts). An unmapped
// value falls back to the neutral secondary variant.
const OUTCOME_VARIANT: Record<string, 'default' | 'destructive' | 'secondary'> = {
  [ElectionOutcomeResult.Won]: 'default',
  [ElectionOutcomeResult.Lost]: 'destructive',
  [ElectionOutcomeResult.Withdrew]: 'secondary',
};

/**
 * ADR-076 (#627) — the per-subsidiary won/lost admin view. A read-only table of every campaign's
 * settled (terminal) election outcome across the subsidiary: campaign · outcome · date recorded.
 * Platform-admin only (the route guards it; the API 403s a non-admin). Subsidiary-WIDE — it is
 * deliberately not gated on a selected campaign, since the outcome is keyed to the campaign and the
 * read spans the whole subsidiary tenant DB. There is no cross-subsidiary rollup.
 */
export function ElectionOutcomesAdmin() {
  const { data: outcomes, isLoading, isError } = useSubsidiaryElectionOutcomes();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Election outcomes</h1>
        <p className="text-muted-foreground mt-1">
          Settled won / lost / withdrew results across every campaign in this subsidiary.
        </p>
      </div>

      {isLoading ? (
        <div
          className="flex items-center gap-2 text-muted-foreground"
          data-testid="election-outcomes-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading election outcomes…
        </div>
      ) : isError ? (
        <div className="text-sm text-destructive">Could not load election outcomes.</div>
      ) : !outcomes || outcomes.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="election-outcomes-empty">
          No settled outcomes yet. A campaign appears here once an admin records a Won, Lost, or
          Withdrew result.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Date recorded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {outcomes.map((o: SubsidiaryElectionOutcomeDto) => (
              <TableRow key={`${o.campaignId}-${o.stage}`}>
                <TableCell className="font-medium text-foreground">{o.campaignName}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant={OUTCOME_VARIANT[o.result] ?? 'secondary'}>
                      {electionOutcomeResultLabel(o.result)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {electionStageLabel(o.stage)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(o.recordedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
